import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { rolloverAPI } from '../../api/endpoints';
import { formatTimeAgo, formatXPUNT } from '../../utils/helpers';
import { 
  FaArrowLeft, FaUsers, FaClock, FaCheck, 
  FaPlus, FaLock, FaUnlock, FaShare, FaCopy,
  FaChartLine, FaTrophy, FaFire, FaUser
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';
import './RolloverDetail.css';

const RolloverDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState(null);
  const [bets, setBets] = useState([]);
  const [joined, setJoined] = useState(false);
  const [subscribers, setSubscribers] = useState([]);

  const fetchGameData = useCallback(async () => {
    setLoading(true);
    try {
      const [gameRes, betsRes, joinedRes] = await Promise.all([
        rolloverAPI.getSingle(id),
        rolloverAPI.getBets(id),
        isAuthenticated ? rolloverAPI.checkJoined(id) : Promise.resolve({ data: { joined: false } }),
      ]);

      setGame(gameRes.data);
      setBets(betsRes.data || []);
      setJoined(joinedRes.data?.joined || false);
    } catch (error) {
      console.error('Failed to fetch rollover:', error);
      toast.error('Failed to load rollover game');
      navigate('/rollover');
    } finally {
      setLoading(false);
    }
  }, [id, isAuthenticated, navigate]);

  useEffect(() => {
    fetchGameData();
  }, [fetchGameData]);

  const handleJoin = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to join');
      navigate('/login');
      return;
    }

    try {
      await rolloverAPI.join(id);
      setJoined(true);
      toast.success('Joined rollover game!');
      fetchGameData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to join');
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: game?.title || 'XPunters Rollover',
        text: `Check out this rollover game: ${game?.title}`,
        url: url,
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied!');
    }
  };

  const isCreator = game && user && game.creator_id === user.id;
  const canJoin = !joined && game?.status === 'active' && isAuthenticated;

  if (loading) {
    return (
      <div className="rollover-detail-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="rollover-not-found">
        <h2>Rollover game not found</h2>
        <Link to="/rollover" className="btn btn-primary">
          <FaArrowLeft /> Back to Rollover
        </Link>
      </div>
    );
  }

  return (
    <div className="rollover-detail-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="rollover-detail-grid">
        {/* Main Content */}
        <div className="detail-main">
          <div className="detail-card">
            {/* Header */}
            <div className="detail-header">
              <div className="detail-meta">
                <span className={`rollover-status ${game.status}`}>
                  {game.status === 'active' ? '🟢 Active' : 
                   game.status === 'completed' ? '✅ Completed' : 
                   '❌ Failed'}
                </span>
                {game.is_private && (
                  <span className="detail-private"><FaLock /> Private</span>
                )}
              </div>
              <div className="detail-actions">
                <button onClick={handleShare} className="action-btn">
                  <FaShare />
                </button>
                {isCreator && game.status === 'active' && (
                  <Link to={`/rollover/${id}/add-bet`} className="action-btn">
                    <FaPlus /> Add Bet
                  </Link>
                )}
              </div>
            </div>

            <h1 className="detail-title">{game.title}</h1>
            {game.description && (
              <p className="detail-description">{game.description}</p>
            )}

            {/* Stats */}
            <div className="detail-stats">
              <div className="stat-item">
                <FaUsers className="stat-icon" />
                <div>
                  <span className="stat-value">{game.subscribers_count || 0}</span>
                  <span className="stat-label">Followers</span>
                </div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <FaChartLine className="stat-icon" />
                <div>
                  <span className="stat-value">{game.total_bets || 0}</span>
                  <span className="stat-label">Total Bets</span>
                </div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <FaFire className="stat-icon" />
                <div>
                  <span className="stat-value">{game.current_streak || 0}</span>
                  <span className="stat-label">Current Streak</span>
                </div>
              </div>
            </div>

            {/* Join Section */}
            {canJoin && (
              <div className="join-section">
                <button onClick={handleJoin} className="btn btn-primary btn-lg">
                  Join Rollover
                </button>
                <p className="join-hint">Follow this rollover and get notified of new bets</p>
              </div>
            )}

            {joined && (
              <div className="joined-badge">
                <FaCheck /> You're following this rollover
              </div>
            )}

            {/* Bets Timeline */}
            <div className="bets-section">
              <h3>📊 Bet Timeline</h3>
              {bets.length === 0 ? (
                <div className="empty-state-small">
                  <p>No bets added yet</p>
                </div>
              ) : (
                <div className="bets-timeline">
                  {bets.map((bet, index) => (
                    <div key={bet.id} className="bet-timeline-item">
                      <div className="timeline-number">{index + 1}</div>
                      <div className="timeline-content">
                        <Link to={`/bet-codes/${bet.id}`} className="bet-link">
                          <div className="bet-header">
                            <span className="bet-title">{bet.title}</span>
                            <span className="bet-odds">Odds: {bet.odds}</span>
                          </div>
                          <div className="bet-code-display">{bet.code}</div>
                          <div className="bet-footer">
                            <span className="bet-games">{bet.number_of_games} games</span>
                            {bet.is_marked && (
                              <span className={`bet-result ${bet.marked_result}`}>
                                {bet.marked_result === 'won' ? '✅ Won' : '❌ Lost'}
                              </span>
                            )}
                            {!bet.is_marked && (
                              <span className="bet-result pending">⏳ Pending</span>
                            )}
                          </div>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="detail-sidebar">
          <div className="info-card">
            <h4>ℹ️ Rollover Info</h4>
            <div className="info-item">
              <span>Creator</span>
              <span>{game.creator_name || 'Unknown'}</span>
            </div>
            <div className="info-item">
              <span>Status</span>
              <span className={game.status}>{game.status}</span>
            </div>
            <div className="info-item">
              <span>Total Bets</span>
              <span>{game.total_bets || 0}</span>
            </div>
            <div className="info-item">
              <span>Current Streak</span>
              <span className="gold">{game.current_streak || 0}</span>
            </div>
            <div className="info-item">
              <span>Created</span>
              <span>{formatTimeAgo(game.created_at)}</span>
            </div>
          </div>

          {isCreator && game.status === 'active' && (
            <div className="info-card creator-actions">
              <h4>🎯 Creator Actions</h4>
              <Link to={`/rollover/${id}/add-bet`} className="btn btn-primary btn-sm">
                <FaPlus /> Add Next Bet
              </Link>
              {game.total_bets > 0 && (
                <button 
                  className="btn btn-success btn-sm"
                  onClick={async () => {
                    try {
                      await rolloverAPI.complete(id);
                      toast.success('Rollover completed!');
                      fetchGameData();
                    } catch (error) {
                      toast.error('Failed to complete rollover');
                    }
                  }}
                >
                  <FaCheck /> Complete Rollover
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RolloverDetail;